import { useState } from 'react';
import { useAuthContext } from 'contexts/AuthContext';
import { NSDropdown, NSLabel, NSModal } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { Deal } from 'views/Deals/types';
import { useGetDealGroups } from 'views/Deals/hooks/useGetDealGroups';
import { useAssociateDealsToDealGroup } from 'views/Deals/hooks/useAssociateToDealGroup';
import useToast from 'hooks/useToast';

export interface IAssociateDealsToGroupModalProps {
    toggle: () => void;
    isOpen: boolean;
    deals: Deal[];
    onAsociationSuccess?: () => void;
}

const AssociateDealsToGroupModal = ({
    toggle, isOpen, deals, onAsociationSuccess,
}: IAssociateDealsToGroupModalProps) => {
    const { selectedOrganizationId } = useAuthContext();
    const { showSuccess, showError } = useToast();

    const [selectedGroup, setSelectedGroup] = useState<IOptionAnyValue | undefined>();

    const { data: dealGroups, isLoading } = useGetDealGroups(selectedOrganizationId!);
    const { mutate: associateDealsToDealGroup, error: associateError } = useAssociateDealsToDealGroup();

    const cleanData = () => {
        setSelectedGroup(undefined);
    };

    const handleAssociateDeals = () => {
        if (!selectedGroup) {
            showError('Please select a group.');
            return;
        }

        associateDealsToDealGroup(
            {
                orgId: selectedOrganizationId!,
                postData: {
                    associations: deals.map(deal => ({
                        dealId: Number(deal.id),
                        dealGroupId: Number(selectedGroup.value),
                    })),
                },
            },
            {
                onSuccess: () => {
                    toggle();
                    cleanData();
                    onAsociationSuccess?.();
                    showSuccess('Deals successfully associated with the group!');
                },
                onError: error => {
                    showError(associateError?.message || 'Failed to associate deals. Please try again.');
                },
            },
        );
    };

    const dealGroupOptions: IOptionAnyValue[] = dealGroups ? dealGroups.map(group => ({ label: group.name, value: group.id, id: group.id })) : [];

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderClassName="bg-primary modal-colored-header"
            modalHeaderText="Associate Deals to Group"
            modalFooterButtonText="Associate Deals"
            modalFooterButtonClassName="heap-deal-pipeline-add-to-group"
            modalFooterIsButtonFunctionDisabled={!selectedGroup}
            modalFooterIsShown
            size="lg"
            modalFooterButtonFunction={handleAssociateDeals}
            modalFooterCancelButtonFunction={cleanData}
        >
            <NSLabel className="text-dark font-weight-normal pt-1">
                Select an existing group
                <span className="text-danger"> *</span>
            </NSLabel>
            <NSDropdown
                options={dealGroupOptions}
                selectedOption={selectedGroup}
                placeholder={isLoading ? 'Loading groups...' : 'Select a group'}
                handleSelectOption={option => setSelectedGroup(option)}
                isFullWidth
            />
        </NSModal>
    );
};

export default AssociateDealsToGroupModal;
